import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import {
  fetchBroadcasts,
  deleteBroadcast,
  sendBroadcast,
  pauseBroadcast,
  resumeBroadcast,
  fetchBroadcastLogs,
  clearBroadcastLogs
} from '../store/slices/broadcastsSlice';
import './Broadcast.css';

const Broadcast = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { broadcasts, loading, error, broadcastLogs, logsLoading } = useSelector(state => state.broadcasts);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [broadcastToDelete, setBroadcastToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [activeLogsBroadcast, setActiveLogsBroadcast] = useState(null);

  // Fetch broadcasts on mount
  useEffect(() => {
    dispatch(fetchBroadcasts());
  }, [dispatch]);

  // Clear success/error messages after 3 seconds
  useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => setSubmitSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (submitError) {
      const timer = setTimeout(() => setSubmitError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitError]);

  const filteredBroadcasts = broadcasts.filter(broadcast => {
    const matchesSearch = 
      broadcast.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      selectedFilter === 'all' ||
      broadcast.status === selectedFilter;

    return matchesSearch && matchesFilter;
  });

  const handleCreateBroadcast = () => {
    navigate('/broadcast/create');
  };

  const handleEditBroadcast = (broadcast) => {
    navigate('/broadcast/create', { state: { broadcast } });
  };

  const handleDeleteBroadcast = (broadcast) => {
    setBroadcastToDelete(broadcast);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (broadcastToDelete) {
      try {
        await dispatch(deleteBroadcast(broadcastToDelete._id || broadcastToDelete.id)).unwrap();
        setSubmitSuccess('Broadcast deleted successfully');
        setIsDeleteModalOpen(false);
        setBroadcastToDelete(null);
      } catch (err) {
        setSubmitError(err?.message || 'Failed to delete broadcast');
      }
    }
  };

  const handleSendNow = async (broadcast) => {
    try {
      await dispatch(sendBroadcast({ id: broadcast._id || broadcast.id, sendNow: true })).unwrap();
      setSubmitSuccess('Broadcast sent successfully');
    } catch (err) {
      setSubmitError(err?.message || err?.data?.message || 'Failed to send broadcast');
    }
  };

  const handlePauseBroadcast = async (broadcast) => {
    try {
      await dispatch(pauseBroadcast(broadcast._id || broadcast.id)).unwrap();
      setSubmitSuccess('Broadcast paused');
    } catch (err) {
      setSubmitError(err?.message || 'Failed to pause broadcast');
    }
  };

  const handleResumeBroadcast = async (broadcast) => {
    try {
      await dispatch(resumeBroadcast(broadcast._id || broadcast.id)).unwrap();
      setSubmitSuccess('Broadcast resumed');
    } catch (err) {
      setSubmitError(err?.message || 'Failed to resume broadcast');
    }
  };

  const handleViewLogs = (broadcast) => {
    const id = broadcast._id || broadcast.id;
    if (activeLogsBroadcast === id) {
      setActiveLogsBroadcast(null);
      dispatch(clearBroadcastLogs());
      return;
    }
    setActiveLogsBroadcast(id);
    dispatch(fetchBroadcastLogs({ id }));
  };

  const handleCloseLogs = () => {
    setActiveLogsBroadcast(null);
    dispatch(clearBroadcastLogs());
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLogsData = () => {
    const data = broadcastLogs || {};
    return {
      broadcast: data.broadcast || activeLogsData,
      analytics: data.analytics || {},
      recipients: Array.isArray(data.recipients) ? data.recipients : [],
      pagination: data.pagination || {}
    };
  };

  const getRecipientStatus = (recipient) => recipient.messageStatus || recipient.status || 'pending';

  const getRecipientError = (recipient) => recipient.errorMessage || recipient.error || '—';

  const getRecipientPhoneNumber = (recipient) => {
    if (!recipient) return '—';
    if (typeof recipient === 'string') return recipient;
    return recipient.phoneNumber || recipient.phone || '—';
  };

  const getRecipientCustomerName = (recipient) => {
    if (!recipient || typeof recipient !== 'object') return null;
    return recipient.customerName || recipient.name || null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Draft', color: '#6b7280', bg: '#f3f4f6' },
      scheduled: { label: 'Scheduled', color: '#f59e0b', bg: '#fef3c7' },
      sending: { label: 'Sending', color: '#3b82f6', bg: '#dbeafe' },
      paused: { label: 'Paused', color: '#f59e0b', bg: '#fef3c7' },
      completed: { label: 'Completed', color: '#10b981', bg: '#d1fae5' },
      failed: { label: 'Failed', color: '#ef4444', bg: '#fee2e2' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span 
        className="status-badge" 
        style={{ 
          background: config.bg, 
          color: config.color 
        }}
      >
        {config.label}
      </span>
    );
  };

  const totalRecipientsSum = broadcasts.reduce((sum, b) => sum + (b.totalRecipients || b.recipients?.length || 0), 0);
  const completedCount = broadcasts.filter(b => b.status === 'completed').length;
  const scheduledCount = broadcasts.filter(b => b.status === 'scheduled').length;
  const activeLogsData = activeLogsBroadcast ? filteredBroadcasts.find(b => (b._id || b.id) === activeLogsBroadcast) : null;
  const logsData = broadcastLogs ? getLogsData() : null;

  return (
    <div className="broadcast-container">
      {/* Notifications */}
      {submitSuccess && (
        <div className="notification success">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          {submitSuccess}
        </div>
      )}
      {submitError && (
        <div className="notification error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          {submitError}
        </div>
      )}
      {error && (
        <div className="notification error">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          {typeof error === 'object' ? JSON.stringify(error) : error}
        </div>
      )}

      <div className="broadcast-header">
        <div className="header-content">
          <h1>Broadcasts</h1>
          <p>Send messages to multiple customers using WhatsApp templates</p>
        </div>
        <button className="btn-create-broadcast" onClick={handleCreateBroadcast} disabled={loading}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create Broadcast
        </button>
      </div>

      <div className="bc-stats-row">
        <div className="bc-stat-card">
          <span className="bc-stat-card-value">{broadcasts.length}</span>
          <span className="bc-stat-card-label">Total Broadcasts</span>
        </div>
        <div className="bc-stat-card">
          <span className="bc-stat-card-value">{scheduledCount}</span>
          <span className="bc-stat-card-label">Scheduled</span>
        </div>
        <div className="bc-stat-card">
          <span className="bc-stat-card-value">{completedCount}</span>
          <span className="bc-stat-card-label">Completed</span>
        </div>
        <div className="bc-stat-card">
          <span className="bc-stat-card-value">{totalRecipientsSum}</span>
          <span className="bc-stat-card-label">Total Recipients</span>
        </div>
      </div>

      <div className="broadcast-toolbar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search broadcasts by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            disabled={loading}
          />
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${selectedFilter === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('all')}
            disabled={loading}
          >
            All ({broadcasts.length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'draft' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('draft')}
            disabled={loading}
          >
            Draft ({broadcasts.filter(b => b.status === 'draft').length})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'scheduled' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('scheduled')}
            disabled={loading}
          >
            Scheduled ({scheduledCount})
          </button>
          <button
            className={`filter-btn ${selectedFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setSelectedFilter('completed')}
            disabled={loading}
          >
            Completed ({completedCount})
          </button>
        </div>
      </div>

      {/* Inline logs panel (replaces logs modal) */}
      {activeLogsBroadcast && (
        <div className="bc-logs-panel">
          <div className="bc-logs-panel-header">
            <h3>Delivery Logs{logsData?.broadcast ? ` — ${logsData.broadcast.name || logsData.broadcast.templateName || 'Broadcast'}` : ''}</h3>
            <button className="modal-close-btn" onClick={handleCloseLogs}>✕</button>
          </div>
          {logsLoading ? (
            <div className="logs-loading"><div className="spinner"></div><p>Loading logs...</p></div>
          ) : logsData ? (
            <div className="logs-content">
              <div className="logs-analytics">
                {[
                  { label: 'Total', value: logsData.analytics.totalRecipients || 0, color: '#6b7280' },
                  { label: 'Sent', value: logsData.analytics.totalSent || 0, color: '#3b82f6' },
                  { label: 'Delivered', value: logsData.analytics.totalDelivered || 0, color: '#10b981' },
                  { label: 'Read', value: logsData.analytics.totalRead || 0, color: '#8b5cf6' },
                  { label: 'Failed', value: logsData.analytics.totalFailed || 0, color: '#ef4444' }
                ].map(stat => (
                  <div key={stat.label} className="log-stat">
                    <span className="log-stat-value" style={{ color: stat.color }}>{stat.value}</span>
                    <span className="log-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
              <div className="logs-table-wrapper">
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Status</th>
                      <th>Sent At</th>
                      <th>Delivered At</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsData.recipients.map((r, i) => {
                      const status = getRecipientStatus(r);
                      const phoneNumber = getRecipientPhoneNumber(r);
                      const customerName = getRecipientCustomerName(r);
                      return (
                        <tr key={i}>
                          <td className="log-phone">{customerName ? `${customerName} (${phoneNumber})` : phoneNumber}</td>
                          <td>
                            <span className={`log-status-badge ${status}`}>{status}</span>
                          </td>
                          <td className="log-date">{r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}</td>
                          <td className="log-date">{r.deliveredAt ? new Date(r.deliveredAt).toLocaleString() : '—'}</td>
                          <td className="log-error">{getRecipientError(r)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {logsData.pagination?.total > 0 && (
                <div className="logs-pagination">
                  <span>
                    Page {logsData.pagination.page || 1} of {logsData.pagination.pages || 1}
                  </span>
                  <span>
                    Showing {logsData.recipients.length} of {logsData.pagination.total} recipients
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="logs-empty">No log data available.</div>
          )}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading broadcasts...</p>
        </div>
      ) : filteredBroadcasts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📢</div>
          <h3>No broadcasts found</h3>
          <p>Create your first broadcast to send messages to multiple customers</p>
          <button className="btn-create-first" onClick={handleCreateBroadcast}>
            Create Broadcast
          </button>
        </div>
      ) : (
        <div className="bc-table-wrapper">
          <table className="bc-table">
            <thead>
              <tr>
                <th>Broadcast</th>
                <th>Template</th>
                <th>Recipients</th>
                <th>Performance</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBroadcasts.map(broadcast => {
                const id = broadcast._id || broadcast.id;
                return (
                  <tr key={id}>
                    <td>
                      <div className="bc-name-cell">
                        <span className="bc-name-text">{broadcast.name}</span>
                        {getStatusBadge(broadcast.status)}
                      </div>
                    </td>
                    <td className="bc-template-cell">
                      {broadcast.templateId?.displayName || broadcast.templateId?.name || broadcast.templateName || 'Unknown Template'}
                    </td>
                    <td>
                      <span className="bc-recipient-badge">
                        {broadcast.totalRecipients || broadcast.recipients?.length || 0}
                      </span>
                    </td>
                    <td>
                      <div className="bc-performance-cell">
                        {broadcast.totalSent > 0 && <span className="bc-perf-item">Sent {broadcast.totalSent}</span>}
                        {broadcast.totalDelivered > 0 && <span className="bc-perf-item success">Delivered {broadcast.totalDelivered}</span>}
                        {broadcast.totalRead > 0 && <span className="bc-perf-item">Read {broadcast.totalRead}</span>}
                        {broadcast.totalFailed > 0 && <span className="bc-perf-item error">Failed {broadcast.totalFailed}</span>}
                        {!broadcast.totalSent && !broadcast.totalDelivered && !broadcast.totalFailed && (
                          <span className="bc-no-value">—</span>
                        )}
                      </div>
                    </td>
                    <td className="bc-date-cell">
                      {broadcast.scheduledFor ? (
                        <span>Scheduled: {formatDate(broadcast.scheduledFor)}</span>
                      ) : broadcast.startedAt ? (
                        <span>Sent: {formatDate(broadcast.startedAt)}</span>
                      ) : (
                        <span>Created: {formatDate(broadcast.createdAt)}</span>
                      )}
                    </td>
                    <td>
                      <div className="bc-row-actions">
                        {broadcast.status === 'draft' && (
                          <button className="action-btn send-btn" onClick={() => handleSendNow(broadcast)} title="Send Now">
                            Send Now
                          </button>
                        )}
                        {broadcast.status === 'scheduled' && (
                          <button className="action-btn pause-btn" onClick={() => handlePauseBroadcast(broadcast)} title="Pause">
                            Pause
                          </button>
                        )}
                        {broadcast.status === 'paused' && (
                          <button className="action-btn send-btn" onClick={() => handleResumeBroadcast(broadcast)} title="Resume">
                            Resume
                          </button>
                        )}
                        {(broadcast.status === 'completed' || broadcast.status === 'sending') && (
                          <button
                            className={`action-btn logs-btn ${activeLogsBroadcast === id ? 'active' : ''}`}
                            onClick={() => handleViewLogs(broadcast)}
                            title="View Delivery Logs"
                          >
                            Logs
                          </button>
                        )}
                        <button
                          className="action-btn edit-btn"
                          onClick={() => handleEditBroadcast(broadcast)}
                          title="Edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                          Edit
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteBroadcast(broadcast)}
                          title="Delete"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setBroadcastToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        customerName={broadcastToDelete?.name || ''}
        type="broadcast"
      />
    </div>
  );
};

export default Broadcast;

